import { useRef, useState } from 'react';
import { Upload, ImageIcon, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '@/services/api';
import { Button } from '@nepal-ecom/ui';

interface SingleProps {
  label?: string;
  value: string;
  onChange: (url: string) => void;
  aspect?: string;
  helperText?: string;
  className?: string;
}

/**
 * Single-image uploader that posts to the API's /uploads/image endpoint.
 * Stores a relative path like `/uploads/image/<uuid>.jpg`; the admin nginx
 * (and storefront) proxies /uploads/ back to the API for serving.
 */
export function FileImageUploader({
  label = 'Image',
  value,
  onChange,
  aspect = 'aspect-[4/3]',
  helperText,
  className,
}: SingleProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [broken, setBroken] = useState(false);

  const handleFile = async (file: File) => {
    setUploading(true);
    setBroken(false);
    try {
      const res = await adminApi.uploadImage(file);
      const url = res.data?.data?.url;
      if (!url) throw new Error('No URL in upload response');
      onChange(url);
      toast.success('Uploaded');
    } catch (err: any) {
      toast.error('Upload failed', {
        description: err?.response?.data?.message ?? err?.message ?? 'Unknown error',
      });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className={`space-y-2 ${className ?? ''}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-slate-700">{label}</span>
        {value && (
          <button
            type="button"
            onClick={() => onChange('')}
            className="text-xs text-slate-400 hover:text-red-500 inline-flex items-center gap-1"
          >
            <X className="h-3 w-3" /> Clear
          </button>
        )}
      </div>

      <div
        className={`${aspect} relative rounded-lg overflow-hidden border-2 border-dashed border-slate-200 bg-slate-50 flex items-center justify-center`}
      >
        {value && !broken ? (
          <img
            src={value}
            alt={label}
            onError={() => setBroken(true)}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="text-center px-4">
            <ImageIcon className="h-8 w-8 text-slate-300 mx-auto mb-2" />
            <p className="text-xs text-slate-500">{broken ? 'Preview failed to load' : 'No image yet'}</p>
          </div>
        )}

        {uploading && (
          <div className="absolute inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center">
            <Loader2 className="h-6 w-6 text-primary-600 animate-spin" />
          </div>
        )}
      </div>

      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void handleFile(f);
        }}
      />

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => fileRef.current?.click()}
        disabled={uploading}
      >
        <Upload className="h-3.5 w-3.5" /> {uploading ? 'Uploading…' : 'Upload image'}
      </Button>

      {helperText && <p className="text-[11px] text-slate-400">{helperText}</p>}
    </div>
  );
}

// ─── Gallery (multi-image) ───────────────────────────────────────────────────

interface GalleryProps {
  label?: string;
  values: string[];
  onChange: (urls: string[]) => void;
  max?: number;
  helperText?: string;
}

/**
 * Multi-image uploader. Supports drag-and-drop, click-to-pick, reorder via
 * arrow buttons. First image is treated as the primary (thumbnail).
 */
export function FileImageGallery({
  label = 'Images',
  values,
  onChange,
  max = 10,
  helperText,
}: GalleryProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const remaining = Math.max(0, max - values.length);

  const handleFiles = async (rawFiles: FileList | File[]) => {
    const files = Array.from(rawFiles).slice(0, remaining);
    if (files.length === 0) {
      toast.info(`Max ${max} images already uploaded`);
      return;
    }
    setUploading(true);
    try {
      const res = await adminApi.uploadImages(files);
      const uploaded = res.data?.data ?? [];
      const urls = uploaded.map((u) => u.url).filter(Boolean);
      onChange([...values, ...urls]);
      toast.success(`Uploaded ${urls.length} image${urls.length === 1 ? '' : 's'}`);
    } catch (err: any) {
      toast.error('Upload failed', {
        description: err?.response?.data?.message ?? err?.message ?? 'Unknown error',
      });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const remove = (idx: number) => onChange(values.filter((_, i) => i !== idx));
  const move = (idx: number, dir: -1 | 1) => {
    const next = idx + dir;
    if (next < 0 || next >= values.length) return;
    const copy = [...values];
    [copy[idx], copy[next]] = [copy[next], copy[idx]];
    onChange(copy);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-slate-700">
          {label} <span className="text-slate-400">({values.length}/{max})</span>
        </span>
      </div>

      <div
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setIsDragging(false);
          if (e.dataTransfer.files?.length) void handleFiles(e.dataTransfer.files);
        }}
        onClick={() => fileRef.current?.click()}
        className={`
          relative rounded-lg border-2 border-dashed transition-colors cursor-pointer
          ${isDragging ? 'border-primary-500 bg-primary-50' : 'border-slate-200 bg-slate-50 hover:border-slate-300'}
          ${uploading ? 'pointer-events-none' : ''}
          py-6 px-4 text-center
        `}
      >
        <Upload className="h-6 w-6 text-slate-400 mx-auto mb-1.5" />
        <p className="text-sm font-medium text-slate-700">
          {uploading ? 'Uploading…' : 'Drop images or click to browse'}
        </p>
        <p className="text-[11px] text-slate-500 mt-0.5">
          JPEG / PNG / WEBP up to 5MB each · {remaining} slot{remaining === 1 ? '' : 's'} left
        </p>
        {uploading && (
          <div className="absolute inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center rounded-lg">
            <Loader2 className="h-6 w-6 text-primary-600 animate-spin" />
          </div>
        )}
      </div>

      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        multiple
        className="hidden"
        onChange={(e) => {
          if (e.target.files?.length) void handleFiles(e.target.files);
        }}
      />

      {values.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {values.map((url, idx) => (
            <div
              key={url + idx}
              className="group relative aspect-square rounded-lg overflow-hidden ring-1 ring-slate-200 bg-slate-100"
            >
              <img src={url} alt="" className="w-full h-full object-cover" />
              {idx === 0 && (
                <span className="absolute top-1.5 left-1.5 bg-primary-600 text-white text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded">
                  Primary
                </span>
              )}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                <div className="flex items-center gap-1">
                  {idx > 0 && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); move(idx, -1); }}
                      className="h-7 w-7 rounded bg-white/90 text-slate-700 text-xs font-bold hover:bg-white"
                      title="Move left"
                    >
                      ←
                    </button>
                  )}
                  {idx < values.length - 1 && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); move(idx, 1); }}
                      className="h-7 w-7 rounded bg-white/90 text-slate-700 text-xs font-bold hover:bg-white"
                      title="Move right"
                    >
                      →
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); remove(idx); }}
                    className="h-7 w-7 rounded bg-red-500 text-white hover:bg-red-600 flex items-center justify-center"
                    title="Remove"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {helperText && <p className="text-[11px] text-slate-400">{helperText}</p>}
    </div>
  );
}
